import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { eyeIcon, eyeSlashIcon, trashIcon } from '@ld.consumer-portal/data-uri';
import { FILE_UPLOAD_CONFIG } from '@ld.consumer-portal/models';

export interface UploadedFile {
  file: File;
  id: string;
  error?: string;
  notes: string;
  showPreview: boolean;
  showEditNotes: boolean;
  previewUrl: string | null;
  safePreviewUrl: SafeResourceUrl | null;
}

export interface FileWithNotes {
  file: File;
  notes: string;
}

@Component({
  selector: 'ld-file-upload',
  imports: [MatIconModule, TranslocoDirective],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileUploadComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly sanitizer = inject(DomSanitizer);

  protected readonly eyeIcon = eyeIcon;
  protected readonly eyeSlashIcon = eyeSlashIcon;
  protected readonly trashIcon = trashIcon;

  maxFiles = input(FILE_UPLOAD_CONFIG.maxFiles);
  maxSizeMB = input(FILE_UPLOAD_CONFIG.maxSizeMB);
  acceptedTypes = input(FILE_UPLOAD_CONFIG.acceptString);
  filesChanged = output<File[]>();
  filesWithNotesChanged = output<FileWithNotes[]>();

  protected readonly files = signal<UploadedFile[]>([]);
  protected readonly validationError = signal<string | null>(null);
  protected readonly isDragging = signal(false);

  protected readonly allowedExtensions = FILE_UPLOAD_CONFIG.allowedExtensions;
  protected readonly maxSizeBytes = FILE_UPLOAD_CONFIG.maxSizeBytes;

  protected readonly fileCount = computed(() => this.files().length);
  protected readonly canAddMore = computed(() => this.files().length < this.maxFiles());
  protected readonly remainingSlots = computed(() => this.maxFiles() - this.files().length);

  protected readonly validFiles = computed(() =>
    this.files()
      .filter((f: UploadedFile) => !f.error)
      .map((f: UploadedFile) => f.file),
  );

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.files().forEach((f) => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
    });
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  protected onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    const droppedFiles = event.dataTransfer?.files;
    if (droppedFiles) {
      this.processFiles(Array.from(droppedFiles));
    }
  }

  protected onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.processFiles(Array.from(input.files));
      input.value = '';
    }
  }

  reset(): void {
    this.files().forEach((f) => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
    });
    this.files.set([]);
    this.validationError.set(null);
    this.emitFiles();
  }

  protected removeFile(fileId: string): void {
    const file = this.files().find((f: UploadedFile) => f.id === fileId);
    if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);

    this.files.update((files: UploadedFile[]) => files.filter((f: UploadedFile) => f.id !== fileId));
    this.validationError.set(null);
    this.emitFiles();
  }

  protected togglePreview(fileId: string): void {
    this.files.update((files) =>
      files.map((f) => {
        if (f.id !== fileId) return f;
        const showPreview = !f.showPreview;
        let { previewUrl, safePreviewUrl } = f;

        if (showPreview && !previewUrl && !f.error) {
          previewUrl = URL.createObjectURL(f.file);
          safePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl);
        }

        return { ...f, showPreview, showEditNotes: false, previewUrl, safePreviewUrl };
      }),
    );
  }

  protected editNotes(fileId: string): void {
    this.files.update((files) =>
      files.map((f) => (f.id === fileId ? { ...f, showEditNotes: true, showPreview: false } : f)),
    );
  }

  protected dismissNotes(fileId: string): void {
    this.files.update((files) => files.map((f) => (f.id === fileId ? { ...f, showEditNotes: false } : f)));
    this.emitFiles();
  }

  protected onNotesChange(fileId: string, notes: string): void {
    this.files.update((files) => files.map((f) => (f.id === fileId ? { ...f, notes } : f)));
  }

  protected isPreviewable(file: File): boolean {
    return file.type.startsWith('image/') || file.type === 'application/pdf';
  }

  protected formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private processFiles(newFiles: File[]): void {
    this.validationError.set(null);

    const currentFiles = this.files();
    const availableSlots = this.maxFiles() - currentFiles.length;

    if (newFiles.length > availableSlots) {
      this.validationError.set(
        this.transloco.translate('fileUploadErrorsMaxFilesExceeded', {
          available: availableSlots,
          max: this.maxFiles(),
        }),
      );
      newFiles = newFiles.slice(0, availableSlots);
    }

    const processedFiles: UploadedFile[] = newFiles.map((file) => {
      const sanitizedFile = this.sanitizeFile(file);
      const uploadedFile: UploadedFile = {
        file: sanitizedFile,
        id: this.generateId(),
        notes: '',
        showPreview: false,
        showEditNotes: false,
        previewUrl: null,
        safePreviewUrl: null,
      };

      const extension = sanitizedFile.name.split('.').pop()?.toLowerCase() || '';
      const isValidExtension = (this.allowedExtensions as readonly string[]).includes(extension);
      const isValidMimeType = this.isAllowedMimeType(sanitizedFile.type);

      if (!isValidExtension && !isValidMimeType) {
        uploadedFile.error = this.transloco.translate('fileUploadErrorsInvalidFileType', {
          allowed: this.allowedExtensions.join(', ').toUpperCase(),
        });
      } else if (sanitizedFile.size > this.maxSizeBytes) {
        uploadedFile.error = this.transloco.translate('fileUploadErrorsFileTooLarge', { maxSize: this.maxSizeMB() });
      }

      return uploadedFile;
    });

    this.files.update((files: UploadedFile[]) => [...files, ...processedFiles]);
    this.emitFiles();
  }

  private isAllowedMimeType(mimeType: string): boolean {
    return (FILE_UPLOAD_CONFIG.allowedMimeTypes as readonly string[]).includes(mimeType);
  }

  private sanitizeFile(file: File): File {
    const lastDot = file.name.lastIndexOf('.');
    const nameWithoutExt = lastDot > 0 ? file.name.substring(0, lastDot) : file.name;
    const ext = lastDot > 0 ? file.name.substring(lastDot) : '';
    const sanitizedName = nameWithoutExt.replace(/[^a-zA-Z0-9._-]/g, '_') + ext;

    if (sanitizedName !== file.name) {
      return new File([file], sanitizedName, { type: file.type, lastModified: file.lastModified });
    }
    return file;
  }

  private emitFiles(): void {
    this.filesChanged.emit(this.validFiles());
    this.filesWithNotesChanged.emit(
      this.files()
        .filter((f: UploadedFile) => !f.error)
        .map((f: UploadedFile) => ({ file: f.file, notes: f.notes })),
    );
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2, 11);
  }
}
